import React, { useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import {
  ChevronRight, ShieldCheck, BadgePercent, TrendingUp, Users, Globe2,
  Sparkles, BarChart3, MessageCircleHeart, Clock, CheckCircle2, IndianRupee,
  Building2, Home, Palmtree, Tent, Star,
} from 'lucide-react';
import { toast } from 'sonner';
import { useSiteContent } from '../hooks/useSiteContent';
import { dangerousSafeHtml } from '../utils/sanitizeHtml';

const BENEFITS = [
  {
    icon: Globe2,
    title: 'Reach North India travellers',
    desc: 'Get instant exposure to thousands of guests across Punjab, Haryana & Chandigarh searching localliink.com every day.',
  },
  {
    icon: BadgePercent,
    title: '0% sign-up fee · pay-per-stay',
    desc: 'Free to list. You only pay a small commission when a booking actually checks in. Zero risk to get started.',
  },
  {
    icon: ShieldCheck,
    title: 'Trusted by 32+ properties',
    desc: 'Verified reviews, secure pay-at-hotel model, and a dedicated hotel admin built for North-Indian hospitality.',
  },
  {
    icon: TrendingUp,
    title: 'Visibility Booster',
    desc: 'Run sponsored placements during low season and watch your bookings climb. You stay in full control of spend.',
  },
];

const STATS = [
  { value: '32+', label: 'Hotels live' },
  { value: '15k+', label: 'Monthly guests' },
  { value: '4.7★', label: 'Avg. guest rating' },
  { value: '< 24h', label: 'Onboarding time' },
];

const STEPS = [
  {
    n: '01',
    title: 'Tell us about your property',
    desc: 'Name, address, photos, room types and rates. Our hotel admin walks you through every step.',
  },
  {
    n: '02',
    title: 'We verify within 24 hours',
    desc: 'Our local team checks documents and quality. We may visit nearby properties for inspection.',
  },
  {
    n: '03',
    title: 'Go live and accept bookings',
    desc: 'Manage availability, prices, and guests from a single dashboard. Get paid at check-in.',
  },
];

const STAR_TIERS = [
  { stars: 1, label: '1-Star', tagline: 'Basic budget stays', desc: 'Clean, minimal amenities. Great for short overnight stops.' },
  { stars: 2, label: '2-Star', tagline: 'Standard with en-suite', desc: 'Comfortable rooms, en-suite bathrooms, basic services.' },
  { stars: 3, label: '3-Star', tagline: 'Mid-market comfort', desc: 'Daily housekeeping, restaurant, modern in-room amenities.' },
  { stars: 4, label: '4-Star', tagline: 'Upscale experience', desc: 'Multiple dining, spa, pool, and high-quality decor.' },
  { stars: 5, label: '5-Star', tagline: 'Premium luxury', desc: '24h room service, personalised concierge, top-tier facilities.' },
];

const TESTIMONIALS = [
  {
    quote: 'In our first month with localliink.com we filled 38 rooms that would have stayed empty. The pay-at-hotel model is exactly what Indian travellers trust.',
    name: 'Harpreet Singh',
    title: 'Owner · Heritage Haveli, Patiala',
    rating: 5,
  },
  {
    quote: 'The hotel admin is so simple my front-desk staff learned it in an hour. And the support team picks up the phone within 2 rings — a rarity.',
    name: 'Anita Sharma',
    title: 'GM · Sector 17 Boutique, Chandigarh',
    rating: 5,
  },
  {
    quote: 'Visibility Booster paid for itself in two weekends. We jumped from page 3 to the top of Amritsar listings.',
    name: 'Rajbir Khurana',
    title: 'Director · Mall Road Inn, Amritsar',
    rating: 5,
  },
];

const FAQS = [
  {
    q: 'How much does it cost to list my property?',
    a: 'It is completely free to create your listing. We charge a small commission only when a guest actually checks in. No subscription, no setup fees.',
  },
  {
    q: 'When do I get paid?',
    a: 'Guests pay you directly at check-in (Pay at Hotel). At the end of every month, you settle commission with localliink.com via a single invoice in the hotel admin.',
  },
  {
    q: 'Can I list a homestay or a small guest house?',
    a: 'Absolutely. We welcome hotels, homestays, resorts, heritage stays, farm-stays, and adventure camps across Punjab, Haryana and Chandigarh.',
  },
  {
    q: 'Do you provide professional photography?',
    a: 'For partners in tier-1 cities (Chandigarh, Amritsar, Gurugram, Ludhiana) we offer free photoshoots once your listing is verified. Other cities — coming soon.',
  },
  {
    q: 'How long does verification take?',
    a: 'Most properties are verified and live within 24 hours. Heritage and large resorts may take up to 3 working days for an on-site visit.',
  },
];

export const ListYourPropertyPage = () => {
  const { t } = useTranslation();
  const { sections: c } = useSiteContent('list-your-property');
  const navigate = useNavigate();
  const [form, setForm] = useState({ propertyName: '', city: '', email: '', phone: '' });
  const [openFaq, setOpenFaq] = useState(0);

  const handleStartListing = () => {
    navigate('/register', { state: { role: 'hotel', prefill: form } });
    toast.success('Almost there! Create your partner account.');
  };

  const handleQuickSubmit = (e) => {
    e.preventDefault();
    if (!form.propertyName || !form.city || !form.email) {
      toast.error('Please fill property name, city and email');
      return;
    }
    handleStartListing();
  };

  return (
    <div className="bg-white" data-testid="list-your-property-page">
      {/* ============== HERO ============== */}
      <section className="relative overflow-hidden bg-gradient-to-br from-[#0F1B2D] via-[#152540] to-[#0F1B2D] text-white">
        <div className="absolute inset-0 pointer-events-none">
          <div className="absolute -top-32 -right-32 w-[28rem] h-[28rem] bg-[#FF0059] rounded-full opacity-20 blur-3xl" />
          <div className="absolute -bottom-32 -left-32 w-[28rem] h-[28rem] bg-[#FFCB00] rounded-full opacity-10 blur-3xl" />
        </div>

        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-14 lg:py-20">
          <div className="grid lg:grid-cols-2 gap-10 lg:gap-16 items-center">
            {/* Left copy */}
            <div data-testid="hero-copy">
              <div className="inline-flex items-center gap-2 px-3 py-1.5 rounded-full bg-white/10 backdrop-blur border border-white/15 text-xs font-bold mb-5">
                <Sparkles className="h-3.5 w-3.5 text-[#FFCB00]" />
                JOIN 32+ HOTELS ACROSS NORTH INDIA
              </div>
              <h1 className="text-4xl sm:text-5xl lg:text-6xl font-black tracking-tight leading-[1.05]" data-testid="lyp-title">
                {c.hero_title || (
                  <>
                    List your property on{' '}
                    <span className="text-[#FF0059]">localliink</span>
                    <span className="text-white">.com</span>
                  </>
                )}
              </h1>
              {c.hero_subtitle_html ? (
                <div
                  className="mt-5 text-base sm:text-lg text-white/80 max-w-xl prose prose-invert prose-sm prose-p:my-0"
                  dangerouslySetInnerHTML={dangerousSafeHtml(c.hero_subtitle_html)}
                  data-testid="lyp-subtitle"
                />
              ) : (
                <p className="mt-5 text-base sm:text-lg text-white/80 max-w-xl">
                  Reach lakhs of travellers booking stays in Punjab, Haryana and Chandigarh.
                  Free to list. Pay only when a guest actually checks in.
                </p>
              )}

              <div className="mt-7 flex flex-wrap items-center gap-3">
                <button
                  onClick={handleStartListing}
                  className="inline-flex items-center gap-2 bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold px-6 sm:px-7 py-3.5 rounded-full text-sm sm:text-base transition-colors shadow-lg shadow-[#FF0059]/30"
                  data-testid="hero-start-listing-btn"
                >
                  {c.cta_button || "Start listing — it's free"} <ChevronRight className="h-4 w-4" />
                </button>
                <a
                  href="#how-it-works"
                  className="inline-flex items-center gap-2 text-white/90 hover:text-white font-bold px-5 py-3 rounded-full border border-white/20 hover:border-white/40 transition-colors text-sm"
                  data-testid="hero-learn-more-link"
                >
                  How it works
                </a>
              </div>

              <div className="mt-8 grid grid-cols-4 gap-3 sm:gap-5 max-w-md" data-testid="hero-stats">
                {STATS.map((s) => (
                  <div key={s.label} className="text-left">
                    <div className="text-xl sm:text-2xl font-black text-[#FFCB00]">{s.value}</div>
                    <div className="text-[10px] sm:text-xs text-white/60 font-semibold uppercase tracking-wider mt-0.5 leading-tight">{s.label}</div>
                  </div>
                ))}
              </div>
            </div>

            {/* Right: quick form */}
            <div className="lg:justify-self-end w-full max-w-md" data-testid="hero-quick-form">
              <div className="bg-white text-black rounded-3xl p-6 sm:p-7 shadow-2xl">
                <h3 className="text-xl sm:text-2xl font-black tracking-tight mb-1">Get started in 60 seconds</h3>
                <p className="text-xs text-gray-500 mb-5">No payment. No long forms. Just a quick intro.</p>
                <form className="space-y-3.5" onSubmit={handleQuickSubmit}>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Property name</label>
                    <input
                      type="text"
                      value={form.propertyName}
                      onChange={(e) => setForm({ ...form, propertyName: e.target.value })}
                      placeholder="e.g. Heritage Haveli"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#FF0059] focus:ring-2 focus:ring-[#FFE5EE] outline-none text-sm font-medium transition-colors"
                      data-testid="form-property-name-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">City</label>
                    <input
                      type="text"
                      value={form.city}
                      onChange={(e) => setForm({ ...form, city: e.target.value })}
                      placeholder="e.g. Amritsar"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#FF0059] focus:ring-2 focus:ring-[#FFE5EE] outline-none text-sm font-medium transition-colors"
                      data-testid="form-city-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Email</label>
                    <input
                      type="email"
                      value={form.email}
                      onChange={(e) => setForm({ ...form, email: e.target.value })}
                      placeholder="you@hotel.com"
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#FF0059] focus:ring-2 focus:ring-[#FFE5EE] outline-none text-sm font-medium transition-colors"
                      data-testid="form-email-input"
                    />
                  </div>
                  <div>
                    <label className="block text-[11px] font-bold uppercase tracking-wider text-gray-500 mb-1">Phone (optional)</label>
                    <input
                      type="tel"
                      value={form.phone}
                      onChange={(e) => setForm({ ...form, phone: e.target.value })}
                      placeholder="+91 ..."
                      className="w-full px-4 py-2.5 rounded-xl border border-gray-200 focus:border-[#FF0059] focus:ring-2 focus:ring-[#FFE5EE] outline-none text-sm font-medium transition-colors"
                      data-testid="form-phone-input"
                    />
                  </div>
                  <button
                    type="submit"
                    className="w-full mt-2 inline-flex items-center justify-center gap-2 bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold py-3 rounded-full text-sm transition-colors"
                    data-testid="form-submit-btn"
                  >
                    Continue <ChevronRight className="h-4 w-4" />
                  </button>
                  <p className="text-[11px] text-gray-400 text-center pt-1">
                    By continuing you agree to our partner terms.
                  </p>
                </form>
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* ============== BENEFITS ============== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="benefits-section">
        <div className="max-w-2xl mb-12">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF0059] mb-2">Why localliink.com</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-black">
            Built for North-Indian hospitality.
          </h2>
          <p className="text-gray-600 mt-3 text-base sm:text-lg">
            Smart tools, real reviews, and a team that actually understands your market.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-4 gap-5 lg:gap-6">
          {BENEFITS.map((b, i) => (
            <div
              key={b.title}
              className="bg-white border border-gray-200 rounded-2xl p-6 hover:border-[#FF0059] hover:shadow-lg transition-all group"
              data-testid={`benefit-card-${i}`}
            >
              <div className="w-12 h-12 rounded-2xl bg-[#FFE5EE] flex items-center justify-center mb-4 group-hover:bg-[#FF0059] transition-colors">
                <b.icon className="h-6 w-6 text-[#FF0059] group-hover:text-white transition-colors" />
              </div>
              <h3 className="text-lg font-black text-black tracking-tight mb-1.5">{b.title}</h3>
              <p className="text-sm text-gray-600 leading-relaxed">{b.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============== HOW IT WORKS ============== */}
      <section id="how-it-works" className="bg-[#FAFAFA] py-16 lg:py-24" data-testid="how-it-works-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center max-w-2xl mx-auto mb-12">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF0059] mb-2">3 simple steps</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-black">
              Live in less than 24 hours.
            </h2>
          </div>
          <div className="grid md:grid-cols-3 gap-5 lg:gap-7">
            {STEPS.map((s, i) => (
              <div
                key={s.n}
                className="relative bg-white border border-gray-200 rounded-3xl p-7 hover:shadow-xl transition-shadow"
                data-testid={`step-card-${i}`}
              >
                <div className="text-6xl font-black text-[#FFE5EE] leading-none mb-3">{s.n}</div>
                <h3 className="text-xl font-black text-black tracking-tight mb-2">{s.title}</h3>
                <p className="text-sm text-gray-600 leading-relaxed">{s.desc}</p>
                {i < STEPS.length - 1 && (
                  <div className="hidden md:block absolute top-1/2 -right-4 w-8 h-px bg-gray-300" />
                )}
              </div>
            ))}
          </div>
          <div className="mt-10 text-center">
            <button
              onClick={handleStartListing}
              className="inline-flex items-center gap-2 bg-black hover:bg-gray-800 text-white font-bold px-7 py-3.5 rounded-full text-sm transition-colors"
              data-testid="how-it-works-cta-btn"
            >
              Create my listing <ChevronRight className="h-4 w-4" />
            </button>
          </div>
        </div>
      </section>

      {/* ============== STAR CATEGORIES ============== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="star-categories-section">
        <div className="max-w-2xl mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF0059] mb-2">All star ratings welcome</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-black">
            From 1-Star to 5-Star.
          </h2>
          <p className="text-gray-600 mt-3 text-base">
            Pick the tier that matches your property — guests use this to set the right expectation.
          </p>
        </div>
        <div className="grid sm:grid-cols-2 lg:grid-cols-5 gap-4">
          {STAR_TIERS.map((t) => (
            <div
              key={t.stars}
              className="bg-white border border-gray-200 rounded-2xl p-5 hover:border-[#FF0059] hover:shadow-md transition-all"
              data-testid={`star-tier-${t.stars}`}
            >
              <div className="flex items-center gap-0.5 mb-3">
                {Array.from({ length: t.stars }).map((_, i) => (
                  <Star key={i} className="h-4 w-4 fill-[#FFCB00] text-[#FFCB00]" />
                ))}
                {Array.from({ length: 5 - t.stars }).map((_, i) => (
                  <Star key={`empty-${i}`} className="h-4 w-4 text-gray-200" />
                ))}
              </div>
              <h3 className="font-black text-black text-base">{t.label}</h3>
              <p className="text-xs font-bold text-[#FF0059] uppercase tracking-wider mt-0.5">{t.tagline}</p>
              <p className="text-xs text-gray-600 mt-2 leading-relaxed">{t.desc}</p>
            </div>
          ))}
        </div>
      </section>

      {/* ============== EARNINGS / COMMISSION ============== */}
      <section className="bg-black text-white py-16 lg:py-24" data-testid="earnings-section">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid lg:grid-cols-2 gap-10 items-center">
            <div>
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FFCB00] mb-2">Transparent pricing</p>
              <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight leading-[1.05]">
                Pay only when guests <span className="text-[#FF0059]">actually stay.</span>
              </h2>
              <p className="text-white/70 mt-4 text-base sm:text-lg max-w-lg">
                No subscriptions. No listing fees. No surprises. We earn only when you do.
              </p>
              <ul className="mt-7 space-y-3">
                {[
                  '0% sign-up cost',
                  'Standard commission 12–15% (negotiable on volume)',
                  'Pay-at-hotel — keep guest payment same day',
                  'Monthly commission settlement via hotel admin invoice',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-sm sm:text-base">
                    <CheckCircle2 className="h-5 w-5 text-[#FFCB00] flex-shrink-0 mt-0.5" />
                    <span className="text-white/90">{item}</span>
                  </li>
                ))}
              </ul>
            </div>

            <div className="bg-white/5 backdrop-blur border border-white/10 rounded-3xl p-7 sm:p-8" data-testid="earnings-calc">
              <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF0059] mb-3">Sample earnings · 1 room</p>
              <div className="space-y-4">
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <span className="text-white/70 text-sm">Average daily rate</span>
                  <span className="font-bold text-lg inline-flex items-center"><IndianRupee className="h-4 w-4" />3,500</span>
                </div>
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <span className="text-white/70 text-sm">Avg. occupancy via localliink.com</span>
                  <span className="font-bold text-lg">18 nights/mo</span>
                </div>
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <span className="text-white/70 text-sm">Gross monthly revenue</span>
                  <span className="font-bold text-lg inline-flex items-center"><IndianRupee className="h-4 w-4" />63,000</span>
                </div>
                <div className="flex items-center justify-between border-b border-white/10 pb-3">
                  <span className="text-white/70 text-sm">localliink.com commission (13%)</span>
                  <span className="font-bold text-lg text-white/60 inline-flex items-center">- <IndianRupee className="h-4 w-4" />8,190</span>
                </div>
                <div className="flex items-center justify-between pt-2">
                  <span className="text-white text-sm font-bold uppercase tracking-wider">You earn / month</span>
                  <span className="font-black text-2xl text-[#FFCB00] inline-flex items-center"><IndianRupee className="h-5 w-5" />54,810</span>
                </div>
              </div>
              <p className="text-[11px] text-white/40 mt-4 leading-relaxed">
                Indicative figures based on average hotel performance in tier-2 Punjab. Your actual earnings will vary.
              </p>
            </div>
          </div>
        </div>
      </section>

      {/* ============== TESTIMONIALS ============== */}
      <section className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-16 lg:py-24" data-testid="testimonials-section">
        <div className="max-w-2xl mb-10">
          <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF0059] mb-2">Partner stories</p>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-black">
            Hoteliers love working with us.
          </h2>
        </div>
        <div className="grid md:grid-cols-3 gap-5 lg:gap-6">
          {TESTIMONIALS.map((t, i) => (
            <div
              key={t.name}
              className="bg-white border border-gray-200 rounded-3xl p-6 sm:p-7 flex flex-col hover:shadow-lg transition-shadow"
              data-testid={`testimonial-${i}`}
            >
              <div className="flex items-center gap-1 mb-3">
                {Array.from({ length: t.rating }).map((_, k) => (
                  <Star key={k} className="h-4 w-4 fill-[#FFCB00] text-[#FFCB00]" />
                ))}
              </div>
              <p className="text-sm sm:text-base text-gray-800 leading-relaxed flex-1">"{t.quote}"</p>
              <div className="mt-5 pt-4 border-t border-gray-100">
                <p className="font-bold text-black">{t.name}</p>
                <p className="text-xs text-gray-500 mt-0.5">{t.title}</p>
              </div>
            </div>
          ))}
        </div>
      </section>

      {/* ============== FAQS ============== */}
      <section className="bg-[#FAFAFA] py-16 lg:py-24" data-testid="faq-section">
        <div className="max-w-3xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="text-center mb-10">
            <p className="text-xs font-bold uppercase tracking-[0.2em] text-[#FF0059] mb-2">Questions answered</p>
            <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-black">
              FAQs
            </h2>
          </div>
          <div className="space-y-3">
            {FAQS.map((f, i) => (
              <div
                key={f.q}
                className="bg-white border border-gray-200 rounded-2xl overflow-hidden"
                data-testid={`faq-${i}`}
              >
                <button
                  onClick={() => setOpenFaq(openFaq === i ? -1 : i)}
                  className="w-full flex items-center justify-between gap-4 px-5 py-4 text-left hover:bg-gray-50 transition-colors"
                  data-testid={`faq-toggle-${i}`}
                >
                  <span className="font-bold text-black text-sm sm:text-base">{f.q}</span>
                  <ChevronRight className={`h-5 w-5 text-gray-400 flex-shrink-0 transition-transform ${openFaq === i ? 'rotate-90 text-[#FF0059]' : ''}`} />
                </button>
                {openFaq === i && (
                  <div className="px-5 pb-5 -mt-1 text-sm text-gray-600 leading-relaxed" data-testid={`faq-answer-${i}`}>
                    {f.a}
                  </div>
                )}
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* ============== FINAL CTA ============== */}
      <section className="py-20" data-testid="final-cta">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-black tracking-tight text-black leading-[1.1]">
            Ready to fill those empty rooms?
          </h2>
          <p className="text-gray-600 mt-4 text-base sm:text-lg max-w-2xl mx-auto">
            Join 32+ partner hotels growing with localliink.com. It's free to list, takes 60 seconds to start.
          </p>
          <div className="mt-7 flex flex-wrap items-center justify-center gap-3">
            <button
              onClick={handleStartListing}
              className="inline-flex items-center gap-2 bg-[#FF0059] hover:bg-[#CC0047] text-white font-bold px-7 py-4 rounded-full text-base transition-colors shadow-xl shadow-[#FF0059]/20"
              data-testid="final-start-btn"
            >
              Start listing — it's free <ChevronRight className="h-5 w-5" />
            </button>
            <a
              href="mailto:partners@localliink.com"
              className="inline-flex items-center gap-2 text-black font-bold px-6 py-4 rounded-full border-2 border-black/10 hover:border-black transition-colors text-base"
              data-testid="final-talk-btn"
            >
              Talk to our team
            </a>
          </div>
        </div>
      </section>
    </div>
  );
};

export default ListYourPropertyPage;
